import { AI_RESPONSE_RATING_MAX, AI_RESPONSE_RATING_MIN } from "@/api/services/AiUsage";

// When the chat asks the user to rate an answer, counted in COMPLETED assistant responses of one
// conversation (the count resets with the chat): the very first answer, then every 5th (5, 10, 15, ...).
// The first one catches the short lookups most chats are; the fixed cadence samples the long ones
// without asking on every answer, which would train people to ignore the row.
export const RATING_PROMPT_EVERY_N = 5;

// Stream part the agent service writes at the start of each turn. It is TRANSIENT — it reaches the
// SDK's onData callback but never lands in `message.parts` — so the chat has to catch it as it passes
// and remember which assistant message it belongs to. Its payload is the usage row's `requestId`.
export const INTERACTION_START_PART_TYPE = "data-interaction_start";

// One label per score, index = score - 1. Written for an answer, not for the product.
export const RATING_LABELS = ["Not helpful", "Somewhat helpful", "Okay", "Helpful", "Excellent"] as const;

export const RATING_SCORES = Array.from(
  { length: AI_RESPONSE_RATING_MAX - AI_RESPONSE_RATING_MIN + 1 },
  (_, index) => AI_RESPONSE_RATING_MIN + index
);

/** Whether the `completedResponseCount`-th completed assistant response (1-based) should carry a rating prompt. */
export function shouldPromptForRating(completedResponseCount: number): boolean {
  if (!Number.isInteger(completedResponseCount) || completedResponseCount < 1) return false;
  return completedResponseCount === 1 || completedResponseCount % RATING_PROMPT_EVERY_N === 0;
}

export function isValidRating(rating: number): boolean {
  return Number.isInteger(rating) && rating >= AI_RESPONSE_RATING_MIN && rating <= AI_RESPONSE_RATING_MAX;
}

/** The label for a committed score, or an empty string outside the scale. */
export function ratingLabel(rating: number): string {
  return isValidRating(rating) ? RATING_LABELS[rating - AI_RESPONSE_RATING_MIN] : "";
}

// The payload is `unknown` as far as the SDK is concerned, so validate it rather than trust it: the
// agent service sends an empty string when it has no usage tracking, and an empty id is nothing the
// backend could match — treat it as "this turn cannot be rated".
export function readInteractionId(payload: unknown): string | null {
  if (typeof payload !== "object" || payload === null || !("interactionId" in payload)) return null;
  const { interactionId } = payload;
  return typeof interactionId === "string" && interactionId.trim().length > 0 ? interactionId : null;
}
